// Paid-click detection, shared by the SERVER entry-touch cookie
// (lib/leads/first-touch.ts) and the CLIENT first-touch capture
// (lib/analytics.ts).
//
// Deliberately has no "server-only" import: the client capture is the path that
// normally fills a lead's `answers.source`, and the server cookie only fills in
// when the client's localStorage came back empty (private browsing). If only
// one of the two learned to spot a paid click, the marker would almost never
// reach a lead and the whole measurement would be quietly useless — so the rule
// lives in one module that both import.
//
// Why this exists at all: our Meta boosts point at the already-published post
// with no link and no UTM (see createBoost), so a paid click and an organic
// click on the same post are otherwise indistinguishable, and cost per lead was
// dividing ad spend by a denominator full of organic leads. Ad platforms append
// their own click id to the destination URL, and its PRESENCE is the signal.
// PostHog already classifies traffic this way off `$initial_fbclid`.
// Owner-approved over re-creating every boost with a tagged link, because that
// would have changed what the ads look like.
//
// THE ONE NARROWING, also owner-approved ("Bio taps count as free social. A
// click with no marking at all still counts as paid, so a boosted post is never
// missed."): Meta appends fbclid to EVERY outbound link, organic Instagram bio
// taps included, so a click id cannot outrank a medium somebody typed on
// purpose. An explicit organic medium (social and its variants, email) wins over
// the click id; a click id with no medium is still paid; a paid medium is still
// paid. Rows stored before this rule keep the label they were stored with.

/** Label prefix marking a visit that arrived from a paid click. */
export const PAID_SOURCE_PREFIX = "paid:"

/** Ad-platform click-id query params, mapped to the network that sets them. */
export const CLICK_ID_PARAMS: ReadonlyArray<readonly [param: string, network: string]> = [
  ["fbclid", "meta"],
  ["gclid", "google"],
  ["ttclid", "tiktok"],
]

/** utm_medium values that say, on purpose, that the click was NOT bought.
 *  The acquisition envelope's channel hint (lib/acquisition/context.ts) keeps
 *  matching EMAIL_MEDIUMS and SOCIAL_MEDIUMS lists; keep the two in step. */
export const ORGANIC_MEDIUMS: ReadonlySet<string> = new Set([
  "social",
  "social_organic",
  "socialorganic",
  "organic_social",
  "ig",
  "email",
  "e-mail",
  "newsletter",
  "mail",
])

/** True when this utm_medium is an explicit organic one. */
export function isExplicitOrganicMedium(medium: string | null | undefined): boolean {
  return ORGANIC_MEDIUMS.has((medium ?? "").trim().toLowerCase())
}

/**
 * The paid-source label for these query params, or undefined when none of the
 * click ids is present or an explicit organic utm_medium outranks it.
 *
 * Only the presence of a click id is recorded, never its value — it is an
 * opaque platform token, and keeping it out means these labels stay PII-free
 * like every other value in this lane.
 */
export function detectPaidSourceLabel(params: URLSearchParams): string | undefined {
  if (isExplicitOrganicMedium(params.get("utm_medium"))) return undefined
  for (const [param, network] of CLICK_ID_PARAMS) {
    if (params.get(param)) return `${PAID_SOURCE_PREFIX}${network}`
  }
  return undefined
}
